'use client';

import React, { useEffect, useState, useRef } from 'react';
import { slugify } from '@/lib/utils';

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

interface StickyTOCProps {
  body: any[];
  postId: string;
}

export default function StickyTOC({ body, postId }: StickyTOCProps) {
  const [headings, setHeadings] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [pinnedId, setPinnedId] = useState<string>('');
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (activeId && listRef.current) {
      const activeElement = listRef.current.querySelector(`[data-id="${activeId}"]`) as HTMLElement;
      if (activeElement) {
        const scrollPos = activeElement.offsetTop - (listRef.current.clientHeight / 2) + (activeElement.clientHeight / 2);
        listRef.current.scrollTo({ top: scrollPos, behavior: 'smooth' });
      }
    }
  }, [activeId]);

  // Extract headings
  useEffect(() => {
    if (!body || !Array.isArray(body)) return;

    const extractedHeadings: TOCItem[] = [];
    body.forEach((block) => {
      if (block._type === 'block' && (block.style === 'h2' || block.style === 'h3')) {
        const text = block.children?.map((c: any) => c.text).join('') || '';
        const id = slugify(text);
        if (id) {
          extractedHeadings.push({
            id,
            text,
            level: block.style === 'h2' ? 2 : 3,
          });
        }
      }
    });
    setHeadings(extractedHeadings);

    // Check local storage for pinned ID
    const savedPin = localStorage.getItem(`wpp_pin_${postId}`);
    if (savedPin) {
      setPinnedId(savedPin);
    }
  }, [body, postId]);

  const [progress, setProgress] = useState(0);

  // Scroll tracking for active highlight and progress
  useEffect(() => {
    if (headings.length === 0) return;

    const handleScroll = () => {
      // Calculate Progress
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = docHeight > 0 ? (scrollY / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, currentProgress)));

      // Calculate Active Highlight
      let currentActiveId = '';
      for (const heading of headings) {
        const element = document.getElementById(heading.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          // If the heading is above the middle of the screen
          if (rect.top <= window.innerHeight / 2) {
            currentActiveId = heading.id;
          }
        }
      }
      if (currentActiveId) {
        setActiveId(currentActiveId);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Init

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const handlePin = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (pinnedId === id) {
      // Unpin
      setPinnedId('');
      localStorage.removeItem(`wpp_pin_${postId}`);
    } else {
      // Pin
      setPinnedId(id);
      localStorage.setItem(`wpp_pin_${postId}`, id);
    }
  };

  const handleScrollTo = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100; // Offset for fixed header
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (headings.length === 0) return null;

  return (
    <div className="bg-muted/30 border border-border p-6 rounded-sm">
      <div className="flex items-center justify-between mb-6 border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          <h3 className="font-bold uppercase tracking-widest text-sm text-foreground">Table of Contents</h3>
        </div>
        {/* Progress Ring */}
        <div className="relative w-8 h-8 flex items-center justify-center">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <path
              className="text-muted/20"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            />
            <path
              className="text-brand transition-all duration-300 ease-out"
              strokeDasharray={`${progress}, 100`}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            />
          </svg>
          <span className="absolute text-[8px] font-bold text-foreground">{Math.round(progress)}%</span>
        </div>
      </div>
      
      <ul ref={listRef} className="space-y-3 max-h-[45vh] overflow-y-auto pr-2 custom-scrollbar">
        {headings.map((heading) => (
          <li 
            key={heading.id}
            data-id={heading.id} 
            className={`relative group transition-colors ${
              heading.level === 3 ? 'ml-4 text-sm' : 'font-medium text-sm'
            } ${
              activeId === heading.id ? 'text-brand' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <a 
              href={`#${heading.id}`}
              onClick={(e) => handleScrollTo(heading.id, e)}
              className="block pr-8"
            >
              {heading.text}
            </a>
            
            <button 
              onClick={(e) => handlePin(heading.id, e)}
              className={`absolute right-0 top-0.5 p-1 rounded-sm transition-all ${
                pinnedId === heading.id 
                  ? 'text-brand opacity-100 bg-brand/10'
                  : 'text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground hover:bg-muted'
              }`}
              title={pinnedId === heading.id ? "Unpin this section" : "Pin this to resume later"}
            >
              <svg className={`w-3.5 h-3.5 ${pinnedId === heading.id ? 'fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </button>
          </li>
        ))}
      </ul>

      {pinnedId && (
        <div className="mt-6 pt-4 border-t border-border">
          <button 
            onClick={(e) => handleScrollTo(pinnedId, e)}
            className="w-full py-2 px-3 bg-brand/10 text-brand text-xs font-bold uppercase tracking-wider rounded-md hover:bg-brand hover:text-white transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
            </svg>
            Resume Reading
          </button>
        </div>
      )}
    </div>
  );
}
